import type { TFunction } from 'i18next'

// ─── Thinking step key → message pool mapping ───────────────────────────────
// The server emits keys like "UNDERSTANDING", "SEARCHING_KB", "TOOL:run_report".
// This module picks a random translated message for each key.

function pickRandom(pool: string[]): string {
  return pool[Math.floor(Math.random() * pool.length)]!
}

/** Map well-known tool names to friendly labels */
const TOOL_LABELS: Record<string, string> = {
  search_knowledge_base: 'knowledge search',
  query_audit_events: 'audit query',
  aggregate_audit_events: 'audit aggregation',
  find_navigation_links: 'navigation lookup',
  search_clients: 'client search',
  get_client_memberships: 'membership lookup',
  get_membership_cancellation_info: 'cancellation info',
  get_service_suspension_info: 'suspension info',
  propose_input: 'input collection',
  propose_action: 'action proposal',
  execute_action: 'action execution',
  create_knowledge_article: 'article creation',
  update_knowledge_article: 'article update',
  create_support_ticket: 'support ticket',
  run_report: 'report',
  query_bigquery: 'data query',
}

function formatToolName(toolName: string): string {
  return TOOL_LABELS[toolName] || toolName.replace(/_/g, ' ')
}

/**
 * Resolve a thinking step key (from the server) to a random user-facing message.
 * Falls back to the raw key if unknown.
 */
export function resolveThinkingStepMessage(key: string, t: TFunction): string {
  // Handle TOOL:<toolName> keys
  if (key.startsWith('TOOL:')) {
    const toolName = key.slice(5)
    const label = formatToolName(toolName)
    const pool = [
      t('supportAssistant.thinkingSteps.tool.0', {
        tool: label,
        defaultValue: `Flowing through ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.1', {
        tool: label,
        defaultValue: `Steady pace through ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.2', {
        tool: label,
        defaultValue: `Breathing into ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.3', {
        tool: label,
        defaultValue: `Holding the pose — ${label} in progress...`,
      }),
      t('supportAssistant.thinkingSteps.tool.4', {
        tool: label,
        defaultValue: `Moving with intention through ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.5', {
        tool: label,
        defaultValue: `Interval set on ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.6', {
        tool: label,
        defaultValue: `Warrior flow through ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.7', {
        tool: label,
        defaultValue: `Vinyasa through ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.8', {
        tool: label,
        defaultValue: `Steady flow through ${label}...`,
      }),
      t('supportAssistant.thinkingSteps.tool.9', {
        tool: label,
        defaultValue: `Mindful movement through ${label}...`,
      }),
    ]
    return pickRandom(pool)
  }

  // Static step pools
  const pools: Record<string, string[]> = {
    UNDERSTANDING: [
      t(
        'supportAssistant.thinkingSteps.understanding.0',
        'Taking a mindful breath before we begin...'
      ),
      t('supportAssistant.thinkingSteps.understanding.1', 'Settling into the present moment...'),
      t('supportAssistant.thinkingSteps.understanding.2', 'Finding my centre...'),
      t('supportAssistant.thinkingSteps.understanding.3', 'Grounding into your question...'),
      t('supportAssistant.thinkingSteps.understanding.4', 'Setting an intention for this one...'),
      t('supportAssistant.thinkingSteps.understanding.5', 'Finding my flow state...'),
      t('supportAssistant.thinkingSteps.understanding.6', 'Deep breath in... processing...'),
      t('supportAssistant.thinkingSteps.understanding.7', 'Tuning into your wavelength...'),
      t('supportAssistant.thinkingSteps.understanding.8', 'Gentle warm-up for the mind...'),
      t('supportAssistant.thinkingSteps.understanding.9', 'Sun salutation for the brain...'),
    ],
    SEARCHING_KB: [
      t('supportAssistant.thinkingSteps.searchingKb.0', 'Flowing through the knowledge base...'),
      t('supportAssistant.thinkingSteps.searchingKb.1', 'Gentle jog through the docs...'),
      t('supportAssistant.thinkingSteps.searchingKb.2', 'Sun salutation through the articles...'),
      t('supportAssistant.thinkingSteps.searchingKb.3', 'Moving mindfully through the guides...'),
      t(
        'supportAssistant.thinkingSteps.searchingKb.4',
        'Vinyasa flow through the knowledge base...'
      ),
      t('supportAssistant.thinkingSteps.searchingKb.5', 'Walking meditation through the docs...'),
      t(
        'supportAssistant.thinkingSteps.searchingKb.6',
        'Steady pace through the knowledge base...'
      ),
      t('supportAssistant.thinkingSteps.searchingKb.7', 'Breathwork and browsing...'),
      t('supportAssistant.thinkingSteps.searchingKb.8', 'Graceful flow through the docs...'),
      t('supportAssistant.thinkingSteps.searchingKb.9', 'Mindful movement through the docs...'),
    ],
    SEARCHING_AUDIT: [
      t('supportAssistant.thinkingSteps.searchingAudit.0', 'Flowing through the activity logs...'),
      t('supportAssistant.thinkingSteps.searchingAudit.1', 'Mindful scan of the audit trail...'),
      t('supportAssistant.thinkingSteps.searchingAudit.2', 'Breathing through the log entries...'),
      t('supportAssistant.thinkingSteps.searchingAudit.3', 'Steady flow through your history...'),
      t(
        'supportAssistant.thinkingSteps.searchingAudit.4',
        'Moving with intention through the records...'
      ),
      t(
        'supportAssistant.thinkingSteps.searchingAudit.5',
        'Gentle twist through the activity feed...'
      ),
      t(
        'supportAssistant.thinkingSteps.searchingAudit.6',
        'Slow and steady through the audit trail...'
      ),
      t('supportAssistant.thinkingSteps.searchingAudit.7', 'Grounded review of the activity...'),
      t('supportAssistant.thinkingSteps.searchingAudit.8', 'Restorative scan of the records...'),
      t(
        'supportAssistant.thinkingSteps.searchingAudit.9',
        'Spinal flow through the activity stream...'
      ),
    ],
    FINDING_NAVIGATION: [
      t('supportAssistant.thinkingSteps.findingNavigation.0', 'Flowing to the right page...'),
      t('supportAssistant.thinkingSteps.findingNavigation.1', 'Graceful transition to find it...'),
      t('supportAssistant.thinkingSteps.findingNavigation.2', 'Final flow to the right spot...'),
      t('supportAssistant.thinkingSteps.findingNavigation.3', 'Navigating the path mindfully...'),
      t('supportAssistant.thinkingSteps.findingNavigation.4', 'Steady pace to the destination...'),
      t('supportAssistant.thinkingSteps.findingNavigation.5', 'Gliding to the right section...'),
      t(
        'supportAssistant.thinkingSteps.findingNavigation.6',
        'Smooth hand-off to the right spot...'
      ),
      t(
        'supportAssistant.thinkingSteps.findingNavigation.7',
        'Flowing sequence to your destination...'
      ),
      t(
        'supportAssistant.thinkingSteps.findingNavigation.8',
        'Gentle pivot to the right section...'
      ),
      t('supportAssistant.thinkingSteps.findingNavigation.9', 'Smooth transition to the page...'),
    ],
    CHECKING_ACTION: [
      t('supportAssistant.thinkingSteps.checkingAction.0', 'Checking what I can do here...'),
      t('supportAssistant.thinkingSteps.checkingAction.1', 'Reviewing the options mindfully...'),
      t('supportAssistant.thinkingSteps.checkingAction.2', "Seeing what's in my toolkit..."),
      t('supportAssistant.thinkingSteps.checkingAction.3', 'Preparing with intention...'),
      t('supportAssistant.thinkingSteps.checkingAction.4', 'Reviewing my approach...'),
      t('supportAssistant.thinkingSteps.checkingAction.5', 'Scanning the possibilities...'),
      t('supportAssistant.thinkingSteps.checkingAction.6', 'Pre-flow preparation...'),
      t('supportAssistant.thinkingSteps.checkingAction.7', 'Finding the right variation...'),
      t('supportAssistant.thinkingSteps.checkingAction.8', 'Selecting the best approach...'),
      t('supportAssistant.thinkingSteps.checkingAction.9', 'Preparing the flow...'),
    ],
    READING_ARTICLES: [
      t('supportAssistant.thinkingSteps.readingArticles.0', 'Cool-down reading session...'),
      t('supportAssistant.thinkingSteps.readingArticles.1', 'Stretching into the details...'),
      t('supportAssistant.thinkingSteps.readingArticles.2', 'Zen moment with the docs...'),
      t('supportAssistant.thinkingSteps.readingArticles.3', 'Guided reading meditation...'),
      t('supportAssistant.thinkingSteps.readingArticles.4', 'Gentle flow through the article...'),
      t('supportAssistant.thinkingSteps.readingArticles.5', 'Mindful reading in progress...'),
      t('supportAssistant.thinkingSteps.readingArticles.6', 'Slow and steady through the text...'),
      t('supportAssistant.thinkingSteps.readingArticles.7', 'Stretching my understanding...'),
      t('supportAssistant.thinkingSteps.readingArticles.8', 'Soaking in the information...'),
      t('supportAssistant.thinkingSteps.readingArticles.9', "Child's pose with the content..."),
    ],
    GENERATING: [
      t('supportAssistant.thinkingSteps.generating.0', 'Final stretch — crafting your answer...'),
      t('supportAssistant.thinkingSteps.generating.1', 'Cool-down — writing it up...'),
      t(
        'supportAssistant.thinkingSteps.generating.2',
        'Savasana complete — here comes your answer...'
      ),
      t('supportAssistant.thinkingSteps.generating.3', 'Namaste — wrapping up the response...'),
      t('supportAssistant.thinkingSteps.generating.4', 'Final flow — putting it together...'),
      t('supportAssistant.thinkingSteps.generating.5', 'Finishing with grace...'),
      t('supportAssistant.thinkingSteps.generating.6', 'Gentle cool-down — writing it up...'),
      t('supportAssistant.thinkingSteps.generating.7', 'Finding stillness — answer on the way...'),
      t('supportAssistant.thinkingSteps.generating.8', 'Final breath — response incoming...'),
      t('supportAssistant.thinkingSteps.generating.9', 'Sealing the practice with your answer...'),
    ],
    LOOKING_UP_CLIENT: [
      t('supportAssistant.thinkingSteps.lookingUpClient.0', 'Looking up your client...'),
      t('supportAssistant.thinkingSteps.lookingUpClient.1', 'Checking in on this member...'),
      t('supportAssistant.thinkingSteps.lookingUpClient.2', "Reviewing the client's journey..."),
      t('supportAssistant.thinkingSteps.lookingUpClient.3', 'Pulling up the client file...'),
      t('supportAssistant.thinkingSteps.lookingUpClient.4', 'Quick scan of their profile...'),
      t(
        'supportAssistant.thinkingSteps.lookingUpClient.5',
        "Flowing through the client's history..."
      ),
      t('supportAssistant.thinkingSteps.lookingUpClient.6', 'Checking their membership card...'),
      t('supportAssistant.thinkingSteps.lookingUpClient.7', "Loading the client's dashboard..."),
      t('supportAssistant.thinkingSteps.lookingUpClient.8', 'Mindful review of this member...'),
      t('supportAssistant.thinkingSteps.lookingUpClient.9', 'Tuning into their journey...'),
    ],
    RUNNING_REPORT: [
      t('supportAssistant.thinkingSteps.runningReport.0', 'Crunching the numbers mindfully...'),
      t('supportAssistant.thinkingSteps.runningReport.1', 'Flowing through the data...'),
      t('supportAssistant.thinkingSteps.runningReport.2', 'Breathing through the numbers...'),
      t('supportAssistant.thinkingSteps.runningReport.3', 'Steady pace through the data...'),
      t('supportAssistant.thinkingSteps.runningReport.4', 'Vinyasa through the report...'),
      t('supportAssistant.thinkingSteps.runningReport.5', 'Core work on the data...'),
      t('supportAssistant.thinkingSteps.runningReport.6', 'Balance flow through the figures...'),
      t('supportAssistant.thinkingSteps.runningReport.7', 'Mindful movement through the report...'),
      t('supportAssistant.thinkingSteps.runningReport.8', 'Flow state with the metrics...'),
      t(
        'supportAssistant.thinkingSteps.runningReport.9',
        'Moving with purpose through the numbers...'
      ),
    ],
  }

  const pool = pools[key]
  if (pool) return pickRandom(pool)

  // Fallback: if the key is an old-style full message (backwards compat), show it directly
  return key
}
